import React from 'react';
import { createPortal } from 'react-dom';
import { useI18n } from '../../contexts/I18nContext';

/**
 * Lightweight confirm dialog — we deliberately don't pull shadcn's
 * AlertDialog here because we want full control over keyboard handling
 * and the surrounding admin pages already manage their own modal stack.
 *
 * Portaled to <body> so the overlay anchors to the viewport regardless
 * of ancestor `transform`/`filter` containing-block creation.
 */
export default function ConfirmDialog({ open, title, description, confirmLabel, danger, onConfirm, onCancel, busy }) {
  const { t } = useI18n();
  if (!open) return null;
  return createPortal(
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-ink/40 backdrop-blur-sm p-4"
      onClick={onCancel}
      data-testid="confirm-dialog-overlay"
    >
      <div
        className="bg-white rounded-2xl shadow-soft-lg border border-stone_line max-w-md w-full p-6 animate-fade-up"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
      >
        <h2 className="font-heading text-2xl text-ink">{title}</h2>
        {description && <p className="text-sm text-ink-soft mt-2">{description}</p>}
        <div className="flex justify-end gap-2 mt-6">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            data-testid="confirm-cancel"
            className="px-4 py-2 rounded-lg text-sm text-ink-soft hover:bg-bg-alt disabled:opacity-50"
          >
            {t('cancel')}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            data-testid="confirm-action"
            className={`px-4 py-2 rounded-lg text-sm font-medium text-white transition-colors disabled:opacity-50 ${
              danger ? 'bg-terracotta hover:bg-terracotta-700' : 'bg-sage hover:bg-sage-700'
            }`}
          >
            {busy ? '…' : (confirmLabel || t('confirm'))}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
